import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTES } from '@shared/config/routes.config';
import { DashboardType } from '@shared/models/dashboard.model';
import { Subject } from 'rxjs';
import { LayoutService, ORNavigationService, SubjectAreaService } from '.';
import {
  isDashboardContainerLayoutType,
  LAYOUT_TYPES,
} from '../config/layout.config';
import { SubjectAreaLayout } from '../models/layout.model';
import { ToastrService } from './toaster.service';

@Injectable({
  providedIn: 'root',
})
export class NavigateService {
  loading$: Subject<void> = new Subject<void>();

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private layoutService: LayoutService,
    private subjectAreaService: SubjectAreaService,
    private navigationService: ORNavigationService
  ) {}

  navigateToLayout(layout: SubjectAreaLayout) {
    if (layout.mainKpiId) {
      const url = this.getKpiUrl(
        this.subjectAreaService.activeSubjectArea.subjectAreaId,
        layout.layoutId,
        layout.mainKpiId,
        DashboardType.mainDashboard
      );
      this.router.navigate([url]);
    } else {
      const url = NavigateService.getDashboardUrl(
        this.subjectAreaService.activeSubjectArea.subjectAreaId,
        layout.layoutId
      );
      this.router.navigate([url]);
    }
  }

  public static getMainDashboardURL(
    subjectAreaId: number,
    layoutId: number,
    kpiId?: number,
    isMainDashboard: boolean = true
  ): string {
    if (kpiId && isMainDashboard) {
      return `${ROUTES.subjectArea}/${subjectAreaId}/dashboard/${layoutId}/kpi/${kpiId}/mainDashboard`;
    }
    if (kpiId && !isMainDashboard) {
      return `${ROUTES.subjectArea}/${subjectAreaId}/dashboard/${layoutId}/kpi/${kpiId}/detailDashboard`;
    }
    return `${ROUTES.subjectArea}/${subjectAreaId}/dashboard/${layoutId}`;
  }

  public navigateToSubjectArea(
    subjectAreaId: number,
    layoutId: number,
    kpiId?: number
  ): void {
    if (layoutId === this.layoutService?.activeLayout?.layoutId) {
      this.toastr.info(this.toastr.MESSAGE.layoutAlreadySelected.error);
      return;
    }

    const url: string = NavigateService.getMainDashboardURL(
      subjectAreaId,
      layoutId,
      kpiId
    );

    if (
      subjectAreaId !==
      this.subjectAreaService?.activeSubjectArea?.subjectAreaId
    ) {
      this.navigationService.setNavigationLoading(true);
    }

    this.router.navigate([url]);
  }

  canNavigateToLayout(layout: SubjectAreaLayout): boolean {
    const hasKpis =
      layout.mainKpiId || isDashboardContainerLayoutType(layout.layoutType);

    if (!hasKpis) {
      this.toastr.info(this.toastr.MESSAGE.subjectArea.error.layoutWithoutKpis);
      return false;
    }

    const hasDashboard = layout.hasDashboard;

    if (!hasDashboard) {
      const message =
        layout.layoutType === LAYOUT_TYPES.powerBiDashboard
          ? this.toastr.MESSAGE.layout.error.powerBiDashboardNotFullyConfigured
          : this.toastr.MESSAGE.subjectArea.error.layoutWithoutDashboard;
      this.toastr.info(message);
      return false;
    }
    return true;
  }

  getKpiUrl(
    subjectAreaId: number,
    layoutId: number,
    kpiId: number,
    dashboardType: string
  ) {
    return `${ROUTES.subjectArea}/${subjectAreaId}/dashboard/${layoutId}/kpi/${kpiId}/${dashboardType}`;
  }

  static getSubjectAreaUrl(subjectAreaId: number) {
    return `${ROUTES.subjectArea}/${subjectAreaId}`;
  }

  static getDashboardUrl(subjectAreaId: number, layoutId: number) {
    return `${ROUTES.subjectArea}/${subjectAreaId}/dashboard/${layoutId}`;
  }

  static getLandingUrl(childLandingId: number): string {
    return `${ROUTES.landing}/${childLandingId}`;
  }
}
